import React, { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import fetchOptionsBuilder from "../../fetchOptionBuilder";
import '../../styles/pages/strains_details.scss'
// import { useCookies } from "react-cookie";
import WorkflowContext from "../../workflowContext";
import * as d3 from "d3";
import { Newick } from 'newick';
import CategoryFilterMenu from "../../components/CategoryFilterMenu";
import { Checkbox } from "primereact/checkbox";

function getSessionStorageOrDefault(key, defaultValue) {
    const stored =  sessionStorage.getItem(key);
    if (!stored) {
        return defaultValue;
    }
    return JSON.parse(stored);

}

export default function Phylotree() {
    // const [cookies, setCookie, removeCookie] = useCookies(['auth', 'userid']);
    const workflow = useContext(WorkflowContext);
    const [data, setData] = useState(null);
    const [propOptions, setPropOptions] = useState([]);
    const [metadata, setMetadata] = useState({});
    const [selectedProperty, setSelectedProperty] = useState('');
    const [textFilter, setTextFilter] = useState(/.+/g);
    const [shownStrains, setShownStrains] = useState(null);
    const [attributeTypes, setAttributeTypes] = useState(null);
    const [showNodeLabel, setShowNodeLabel] = useState(true);
    const [strains, setStrains] = useState(null);
    // const [zoomTransform, setZoomTransform] = useState(d3.zoomIdentity);
    const navigate = useNavigate();

    const strainMap = useMemo(() => {
        if (!strains) return null;
        const map = {};
        strains.forEach(strain => {
            map[strain.strain] = strain;
        });
        return map;
    }, [strains]);

    const margin = {
        top: 20,
        right: 90,
        bottom: 30,
        left: 90
    }
    const width = 960 - margin.left - margin.right;
    const height = 700 - margin.top - margin.bottom;
    const outerRadius = width / 2;
    // const innerRadius = outerRadius - 170;

    const [apitoken, setApitoken] = useState(             // eslint-disable-line no-unused-vars
        getSessionStorageOrDefault('apitoken', null)
    );

    const getDataUserId = JSON.parse(sessionStorage.getItem('userid'));       

    // const innerRadius = outerRadius - 170;
    const [innerRadius, setInnerRadius] = useState(outerRadius - 170); //same as innerRadius
    // console.log('innerradius', innerRadius);
    

    // get min/max bootstrapvals to setup bootstrap legend coloarbar
    const getBootstrapVals = (d, barr) => {
        const bstr = parseFloat(d.id);
        if (!isNaN(bstr)){
            barr.push(bstr)
        }
        if (d.branchset) d.branchset.forEach(branch => getBootstrapVals(branch, barr));
    }

    const setBranchColorRange = (treeData) => {
        if (!treeData) return null;
        var barr = []
        getBootstrapVals(treeData, barr);
        const minbstr = barr.length === 0? null : Math.round(Math.min(...barr) / 10) * 10;
        const maxbstr = barr.length === 0? null : Math.round(Math.max(...barr) / 10) * 10;
        if (!isNaN(minbstr) && !isNaN(maxbstr)) {
            return d3.scaleLinear([minbstr, maxbstr], ["brown", "steelblue"]).unknown("black")
        }
        return null;
    }
    
    const colorBranches = (d, bootstrapColors) => {
      let id = d.data.id;
      d.color = bootstrapColors(id);
      if (d.children) d.children.forEach(child => colorBranches(child, bootstrapColors)); 
    }





    useEffect(() => {
        document.title = `Phylotree | Palmona Pathogenomics`
    }, []);

    // get phylotree data
    useEffect(() => {
        if (data) setData(null);
        if (propOptions.length) setPropOptions([]);
        if (selectedProperty) setSelectedProperty('');
        if (Object.keys(metadata).length) setMetadata({});
        if (attributeTypes) setAttributeTypes(null);
        const path = process.env.REACT_APP_VISUALIZATION_API_PATH
        fetch(path + 'clusters/full_tree/tree', fetchOptionsBuilder(apitoken, workflow, getDataUserId))
            .then(res => res.text())
            .then(val => setData(val))
            .catch(err => console.error(err))
        fetch(path + 'straindata/property_list', fetchOptionsBuilder(apitoken, workflow, getDataUserId))
            .then(res => res.json())
            .then(cats => {
                setPropOptions(cats);
                setSelectedProperty(cats[0]);
            })
            .catch(err => console.error(err))
        fetch(path + 'metadata/json', fetchOptionsBuilder(apitoken, workflow, getDataUserId))
            .then(res => res.json())
            .then(val => setMetadata(val))
            .catch(err => console.error(err))
        fetch(path + 'strains/json', fetchOptionsBuilder(apitoken, workflow, getDataUserId))
            .then(res => res.json())
            .then(val => setStrains(val))
            .catch(err => console.error(err))
        fetch(path + 'attribute_types', fetchOptionsBuilder(apitoken, workflow, getDataUserId))
            .then(res => res.json())
            .then(val => setAttributeTypes(val))
            .catch(err => console.error(err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workflow, apitoken, getDataUserId]);
    
    // create category map
    const propertyMap = useMemo(() => {
        if (Object.keys(metadata).length) {
            const newPropertyMap = {};
            Object.keys(metadata).forEach(property => {
                const propVals = new Set();
                Object.values(metadata[property]).forEach(val => {
                    propVals.add(val);
                });
                newPropertyMap[property] = propVals;
            });
            return newPropertyMap;
        } else return null;
    }, [metadata]);

    // create newick tree and filter from menu
    const newickTree = useMemo(() => {
        if (!data || !strainMap) return null;
        const initialTree = new Newick(data)
        // returns a node if it passes the filter or has children that pass the filter, otherwise returns null
        const filterNode = node => {
            // handle leaves
            const id = node.name;
            node.id = id;
            node.name = strainMap[id]?.name;
            if (!node.branchset) {
                if (!node.name.match(textFilter)) return null;
                if (shownStrains && !shownStrains.has(node.name)) return null;
                return node;
            }
            // handle branches
            else {
                const newBranchset = [];
                node.branchset.forEach(branch => {
                    const filteredBranch = filterNode(branch);
                    if (filteredBranch) newBranchset.push(filteredBranch);
                });
                if (newBranchset.length) {
                    node.branchset = newBranchset;
                    return node;
                } else return null;
            }
        }
        const filteredTree = filterNode(initialTree.tree);
        console.log('filtered tree', filteredTree);
        return filteredTree ? [d3.hierarchy(filteredTree, d => d.branchset), setBranchColorRange(filteredTree)]  : null;
    }, [data, shownStrains, textFilter, strainMap]);

    const colorScheme = [];
    if (selectedProperty && propertyMap) {
        const propLength = propertyMap[selectedProperty].size;
        for (let i = 0; i < propLength; i++) {
            colorScheme.push(d3.interpolateRainbow(i / propLength));
        }
    }
    const color = d3.scaleOrdinal(colorScheme);

    const setRadius = (d, y0, k) => {
        d.radius = (y0 += d.data.length) * k;
        if (d.children) d.children.forEach(d => setRadius(d, y0, k));
      }
    
    
    const maxLength = (d) => {
        return d.data.length + (d.children ? d3.max(d.children, maxLength) : 0);
    }

    const linkStep = (startAngle, startRadius, endAngle, endRadius) => {
        const c0 = Math.cos(startAngle = (startAngle - 90) / 180 * Math.PI);
        const s0 = Math.sin(startAngle);
        const c1 = Math.cos(endAngle = (endAngle - 90) / 180 * Math.PI);
        const s1 = Math.sin(endAngle);
        return "M" + startRadius * c0 + "," + startRadius * s0
            + (endAngle === startAngle ? "" : "A" + startRadius + "," + startRadius + " 0 0 " + (endAngle > startAngle ? 1 : 0) + " " + startRadius * c1 + "," + startRadius * s1)
            + "L" + endRadius * c1 + "," + endRadius * s1;
        }

    const linkVariable = (d) => {
        return linkStep(d.source.x, d.source.radius, d.target.x, d.target.radius);
    }

    const linkConstant = (d) => {
        return linkStep(d.source.x, d.source.y, d.target.x, d.target.y);
    }

    const linkExtensionVariable = (d) => {
        return linkStep(d.target.x, d.target.radius, d.target.x, innerRadius);
    }

    const linkExtensionConstant = (d) => {
        return linkStep(d.target.x, d.target.y, d.target.x, innerRadius);
    }

    const nodeText = (svg, root, propval, showNodeLabel) => {
        const { x, y } = root;
        let textlabels = svg.selectAll("text").data(root.leaves())
          .join("text")
          .each(function (d) { })
          .exit().remove();
        svg.append("g")
          .attr("zoomLock", "0")
          .attr("class", "chartelement")
          .selectAll("text")
          .data(root.leaves())
          .join("text")
          .each(function (d) { })
          .attr("x", 10)
          //.attr("y", 5)
          .attr("dy", ".31em")
          .attr("transform", d => `rotate(${d.x - 90}) translate(${d.x < 180 ? innerRadius + 4 : innerRadius + 24},0)${d.x < 180 ? "" : "rotate(180)"}`)
          .attr("text-anchor", d => d.x < 180 ? "start" : "end")
          .text(showNodeLabel === true ? d => d.data.name.replace(/_/g, " ") : ""/* + " , " + " ,," + d.data.metadata[propval]*/)
        //   .style("fill", d => color(metadata[selectedProperty][d.data.name]))
    }

    function adjinnerRadius(scale) {
      setInnerRadius(prevRadius => prevRadius * scale);
    }
    const chart = (treeInfo) => {
        const [treeData, bootstrapColors] = treeInfo;
        const svg = d3.create("svg");
        const root = treeData
          .sum(d => d.branchset ? 0 : 1)
          .sort((a, b) => (a.value - b.value) || d3.ascending(a.data.length, b.data.length));
        const cluster = d3.cluster()
          .size([360, innerRadius])
          .separation((a, b) => 1);
        cluster(root);
        setRadius(root, root.data.length = 0, innerRadius / maxLength(root));
        if (bootstrapColors) colorBranches(root, bootstrapColors);
        svg
          .attr("width", width)
          .attr("height", height)
          .attr("viewBox", [-outerRadius, -outerRadius, width, width])
          .attr("font-family", "sans-serif")
          .attr("font-size", 10)
          .attr("style", "position:absolute; z-index:5;")
          .attr('id', 'phylotree-graph');
        svg.append("g")
        .attr("zoomLock", "0");
          //.call(legend)
        //   .call(zoom);
        svg.append("style").text(`
          .link--active {
            stroke: #000 !important;
            stroke-width: 1.5px;
          }
          .link-extension--active {
            stroke-opacity: .6;
          }
          .label--active {
            font-weight: bold;
          }
          `);
          
          
        const linkExtension = svg.append("g")
          .attr("class", "chartelement")
          .attr("zoomLock", "0")
          .attr("fill", "none")
          .attr("stroke", "#000")
          .attr("stroke-opacity", 0.25)
          .selectAll("path")
          .data(root.links().filter(d => !d.target.children))
          .join("path")
          .each(function (d) { d.target.linkExtensionNode = this; })
          .attr("d", linkExtensionConstant);
    
        const link = svg.append("g")
          .attr("class", "chartelement")
          .attr("zoomLock", "0")
          .attr("fill", "none")
          .attr("stroke", "#000")
          .selectAll("path")
          .data(root.links())
          .join("path")
          .each(function (d) { d.target.linkNode = this; })
          .attr("d", linkConstant)
          .attr("stroke", d => d.target.color)
          .attr("stroke-width", 2)

        const clickNode = e => {
            navigate(`visualizations/strains/detail/${e.target.__data__.data.id}`);
        }

        const mouseoverNode = e => {
            const strain = strainMap[e.target.id.slice(2)];
            const nodeId = strain.strain;
            const nodeName = strain.name;
            d3.select(`#id${nodeId}`)
                .attr("r", 10)
            let tooltipText = `<b>Genome Name: ${nodeName}</b><br>`;
            tooltipText += `<b>${selectedProperty}: ${metadata[selectedProperty][nodeId]}</b><br>`
            for (const property in metadata) {
                if (property !== 'genome_id' && property !== selectedProperty) {
                    tooltipText += `${property}: ${metadata[property][nodeId]}<br>`
                }
            }
            tooltip
                .style("opacity", 1)
                .style("left", (e.pageX) + "px")
                .style("top", (e.pageY) + "px")
                .html(tooltipText)
        }

        const mouseoutNode = e => {
            const nodeId = e.target.id;
            d3.select(`#${nodeId}`)
                .attr("r", 6)
            tooltip
                .style("opacity", 0)
                .html('')
        }

        const tooltip = d3.select('#phylotree-container')
            .append('div')
            .attr("id", "phylotree-tooltip")
            .style("opacity", 0)
            .attr("class", "tooltip")
            .style("backgroud-color", "white")
            .style("border", "solid")
            .style("padding", "5px")
            .style("width", "100px")
            .style("z-index", "10")
            .style("background-color", "white")
            .style("font-size", "12px")
            .style("position", "absolute")
        
        const node = svg.append("g")
          .attr("zoomLock", "1")
          .attr("class", "chartelement")
          .selectAll("g")
          .data(root.leaves())
          .join("g")
          .attr("transform", d => `rotate(${d.x - 90}) translate(${innerRadius + 4},0)${d.x < 180 ? "" : " rotate(180)"}`)
          .style("z-index", "1")
        node.append("text")
          .attr("dy", ".31em")

        if (metadata && selectedProperty) {
        //   const addn = (node) => {
        //     if (node.data && node.data.name) {
        //       const metadatafornode = metaData[node.data.name];
        //       if (metadatafornode) {
        //         node.data.metadata = metadatafornode
        //       }
        //     }
        //     if (node.children) {
        //       node.children.forEach(addn);
        //     }
        //   };
        //   addn(root)
          //commenting the code for the link coloring incase needed in future
        //   link.filter(d => d.target.data.metadata && !d.target.data.children)
        //  .style('stroke',  d => color(d.target.data.metadata[selectedProperty]))
          node.filter(d => !d.children)
            .append("circle")
            .attr("id", d => 'id' + d.data.id)
            .attr("dy", "1em")
            .attr("r", 6)
            .style("fill", d => color(metadata[selectedProperty][d.data.id]))
            .on("click", e => clickNode(e))
            .on("mouseover", e => mouseoverNode(e))
            .on("mouseout", e => mouseoutNode(e))
          try {
            nodeText(svg, root, selectedProperty, showNodeLabel);
          } catch (err) {
            console.error(err);
          }
    
        }
        const update = (checked) => {
          const t = d3.transition().duration(750);
          linkExtension.transition(t).attr("d", checked ? linkExtensionVariable : linkExtensionConstant);
          link.transition(t).attr("d", checked ? linkVariable : linkConstant);
        }

        const handleZoom = e => {
            // console.log('e transform', e.transform);
            // setTimeout(setZoomTransform(e.transform), 100);
            d3.selectAll(".chartelement")
            .attr('transform', e.transform);
            node.attr("transform", d => `translate(${e.transform.x / innerRadius}, ${e.transform.y / innerRadius}) rotate(${d.x - 90}) translate(${innerRadius + 4},0)${d.x < 180 ? "" : " rotate(180)"}`);
            // TODO: make alternate zoom work with state
            // adjinnerRadius(e.transform);
        }

        function doubleclick(){
            d3.selectAll("g[zoomLock='0']")
            .attr("transform", "scale(2)");
            adjinnerRadius(1.5);

        }
        let zoom = d3.zoom()
	      .translateExtent([[-width * 5, -height * 5], [width * 5, height * 5]]) //TODO: this should probably scale by number of nodes
          .on('zoom', handleZoom);
        svg.call(zoom)
        .on("dblclick.zoom",  doubleclick);
        


        return Object.assign(svg.node(), { update });
    }

    // console.log('zoom transform', zoomTransform);

    const bootstrapLegend = (treeInfo) => {
        const [tree, colorScale] = treeInfo;
        var xloc = width,
        yloc = 30,
        barwidth = 250;
        
        // append a defs (for definition) element to your SVG
        var svgLegend = d3.create("svg")
        .attr("width", 80)
        .attr("id", "bootstrap-legend");
        var defs = svgLegend.append('defs');

        // append a linearGradient element to the defs and give it a unique id
        var linearGradient = defs.append('linearGradient')
                .attr('id', 'linear-gradient');

        if (!colorScale) return svgLegend.node();

        const [bmin, bmax] = colorScale.domain();
        
        // horizontal gradient
        linearGradient
        .attr("x1", `${bmin}%`)
        .attr("y1", "0%")
        .attr("x2", `${bmax}%`)
        .attr("y2", "0%");

        // append multiple color stops by using D3's data/enter step
        linearGradient.selectAll("stop")
        .data(colorScale.ticks().map((t, i, n) => ({ offset: `${bmax*i/n.length}%`, color: colorScale(t)})))
        .enter().append("stop")
        .attr("offset", function(d) { 
            return d.offset; 
        })
        .attr("stop-color", function(d) { 
            return d.color; 
        });

        // append title
        svgLegend.append("text")
        .attr("class", "legendTitle")
        .attr("x", xloc)
        .attr("y", yloc - 10)
        .style("text-anchor", "left")
        .text("Bootstrap values");

        // draw the rectangle and fill with gradient
        svgLegend.append("rect")
        .attr("x", xloc)
        .attr("y", yloc)
        .attr("width", barwidth)
        .attr("height", 10)
        .style("fill", "url(#linear-gradient)")

        //create tick marks
        var xLeg = d3.scaleLinear()
        .domain([bmin, bmax])
        .range([0, barwidth]);

        var axisLeg = d3.axisBottom(xLeg)
        .tickValues(colorScale.domain());

        svgLegend
        .append("g")
        .attr("zoomLock", "1")
        .attr("transform", `translate(${xloc}, ${yloc + 10})`)
        .call(axisLeg);

        return svgLegend.node();
    }
    useEffect(() => {
        if (Object.keys(metadata).length && propertyMap) {
            const oldTree = document.getElementById('phylotree-graph');
            const oldTooltip = document.getElementById('phylotree-tooltip');
            const oldBoostrapLegend = document.getElementById('bootstrap-legend');
            if (oldBoostrapLegend) oldBoostrapLegend.remove();
            if (oldTree) oldTree.remove()
            if (oldTooltip) oldTooltip.remove();
            if (newickTree && newickTree[0]) document.getElementById('phylotree-container').append(chart(newickTree), bootstrapLegend(newickTree));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metadata, colorScheme, propertyMap]);

    return (
        <div id='phylotree-container' className="flex-nowrap justify-content-between row">
            <div id='side-display' className="w-20rem">
                <h3>Phylotree</h3>
                <div>
                    <CategoryFilterMenu 
                        attributeTypes={attributeTypes}
                        propOptions={propOptions}
                        selectedProperty={selectedProperty}
                        setSelectedProperty={setSelectedProperty}
                        setTextFilter={setTextFilter}
                        metadata={metadata}
                        shownGenes={shownStrains}
                        setShownGenes={setShownStrains}
                        visibilityToggle={true}
                    />
                    <div className="flex mt-2">
                        <Checkbox onChange={e => setShowNodeLabel(e.checked)} checked={showNodeLabel}/>
                        <p className="ml-1">Display Node Labels</p>
                    </div>
                </div>
            </div>
            {/* <svg style={{ width: "40rem", zIndex: '0'}} className="dforcetree absolute"></svg> */}
            
            <div style={{marginTop:"2rem"}} className="flex-item menu w-7rem" id="category-filter-menu">
                {propertyMap &&
                    <div  style={{position : 'absolute',  paddingLeft : '30px'}}>
                    <span style={{ fontWeight: "bold", marginBottom: "1rem" }}>{selectedProperty || ''}</span>
                    {[...propertyMap[selectedProperty]]?.map(value => (
                        <div>
                        <svg width="20" height="20">
                            <circle cx="10" cy="10" r="8" fill={color(value)}></circle>
                        </svg>
                        <span style={{ color: color(value) }}>{value}</span>
                        </div>
                    ))}
                    </div>
                }
            </div>
        </div>
    )
}